import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../auth/AuthProvider'
import { formatToPar } from '../lib/format'
import { usePageTitle } from '../lib/usePageTitle'

interface Standing {
  user_id: string
  display_name: string
  majors_played: number
  majors_total: number
  score: number
  placement_sum: number
  points: number
  total_to_par_sum: number
  best_finish: number | null
  wins: number
  mode: string
}

interface Group {
  id: string
  name: string
}

interface SidebetRow {
  season: number
  major: string
  player_name: string
}

const MAJOR_ORDER = ["masters", "pga", "us_open", "the_open"] as const
const MAJOR_SHORT: Record<string, string> = {
  masters: "Masters",
  pga: "PGA",
  us_open: "US Open",
  the_open: "The Open",
}

export function Marathon() {
  const { session } = useAuth()
  usePageTitle('Maraton')
  const [seasons, setSeasons] = useState<number[]>([])
  const [season, setSeason] = useState<number | null>(null)
  const [groups, setGroups] = useState<Group[]>([])
  const [groupId, setGroupId] = useState<string | null>(null)
  const [rows, setRows] = useState<Standing[]>([])
  const [sidebet, setSidebet] = useState<SidebetRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function init() {
      const [t, g, sb] = await Promise.all([
        supabase.from('tournaments').select('season').eq('status', 'completed'),
        supabase.from('groups').select('id, name').order('name'),
        supabase.from('archive_major_winners')
          .select('season, major, player_name')
          .order('season', { ascending: false }),
      ])

      const list = [...new Set(((t.data ?? []) as Array<{ season: number }>).map((x) => x.season))]
        .sort((a, b) => b - a)
      setSeasons(list)
      setSeason(list[0] ?? null)
      setGroups((g.data as Group[]) ?? [])

      // Utan det här försvinner rutnätet tyst om vyn inte går att läsa:
      // sb.data blir null, listan blir tom och sektionen renderas bara inte.
      // En segrarlista som inte går att läsa ska säga det.
      setSidebet((sb.data as SidebetRow[]) ?? [])
      if (sb.error) setError(`Segrarlistan kunde inte läsas: ${sb.error.message}`)

      if (list.length === 0) setLoading(false)
    }
    void init()
  }, [])

  const load = useCallback(async () => {
    if (season === null) return
    setLoading(true)
    const { data, error } = await supabase.rpc('marathon', {
      p_season: season,
      p_group_id: groupId,
    })
    if (error) setError(error.message)
    else setRows((data as Standing[]) ?? [])
    setLoading(false)
  }, [season, groupId])

  useEffect(() => {
    void load()
  }, [load])

  const mode = rows[0]?.mode ?? 'placement_sum'
  const scoreLabel = mode === 'points' ? 'Poäng' : 'Placeringssumma'
  const scoreHint =
    mode === 'points'
      ? 'Högst poäng vinner. En missad major ger noll.'
      : 'Lägst summa vinner. En missad major kostar sämsta placering plus ett.'

  return (
    <div className="page stack">
      <div>
        <p className="eyebrow">Säsongen</p>
        <h1>Maratontabellen</h1>
      </div>

      {error && <div className="notice notice--error">{error}</div>}

      {/* Enda historiska uppgiften som är verifierad rad för rad. Poängerna
          ur Excel (archive_marathon/archive_results) visades här tidigare men
          gick inte att lita på — de är borttagna ur vyn, inte ur databasen,
          och kan tas tillbaka en bit i taget allteftersom varje major
          stäms av. */}
      {sidebet.length > 0 && (
        <section className="card stack" id="segrare">
          <h2>Segrare i respektive major</h2>
          <div className="winners">
            <div className="winners__row winners__row--head">
              <span className="winners__season" />
              {MAJOR_ORDER.map((m) => (
                <span key={m} className="winners__cell">{MAJOR_SHORT[m]}</span>
              ))}
            </div>
            {[...new Set(sidebet.map((x) => x.season))].map((s) => (
              <div key={s} className="winners__row">
                <span className="winners__season">{s}</span>
                {MAJOR_ORDER.map((m) => {
                  const namn = sidebet.find((x) => x.season === s && x.major === m)
                  return (
                    <span key={m} className="winners__cell">
                      {namn?.player_name ?? '–'}
                    </span>
                  )
                })}
              </div>
            ))}
          </div>
        </section>
      )}

      {seasons.length === 0 ? (
        <div className="card stack">
          <p className="muted">
            Ingen avslutad major än. Tabellen fylls i takt med att majors får status
            <em> avslutad</em> — första gången vid Masters 2027.
          </p>
        </div>
      ) : (
        <>
          <div className="card stack">
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <div style={{ maxWidth: 160 }}>
                <label htmlFor="season">Säsong</label>
                <select
                  id="season"
                  value={season ?? ''}
                  onChange={(e) => setSeason(Number(e.target.value))}
                >
                  {seasons.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              <div style={{ flex: 1, minWidth: 200 }}>
                <label htmlFor="group">Tabell</label>
                <select
                  id="group"
                  value={groupId ?? ''}
                  onChange={(e) => setGroupId(e.target.value || null)}
                >
                  <option value="">Alla anmälda — globala tabellen</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <p className="muted">{scoreHint}</p>
          </div>

          {loading && <p className="muted">Räknar…</p>}

          {!loading && rows.length === 0 && (
            <div className="card">
              <p className="muted">Ingen har spelat en avslutad major i den här tabellen än.</p>
            </div>
          )}

          {rows.length > 0 && (
            <div className="board">
              {/* Segrar direkt efter namnet: tävlingsledningen räknar
                  segrarna som det som betyder något, och arkivet visar dem
                  först. Då ska den levande tabellen inte gömma dem mellan
                  två statistikkolumner. Ordningen i tabellen avgörs
                  fortfarande av poängen — se seasons.marathon. */}
              <div className="marathon__head">
                <span className="board__pos">#</span>
                <span className="board__name">Deltagare</span>
                <span className="marathon__cell">Segrar</span>
                <span className="marathon__cell marathon__cell--opt">Majors</span>
                <span className="marathon__cell marathon__cell--opt">Bäst</span>
                <span className="marathon__cell marathon__cell--opt">Score</span>
                <span className="marathon__score">{scoreLabel}</span>
              </div>

              {rows.map((r, i) => (
                <div
                  key={r.user_id}
                  className={`marathon__row${r.user_id === session?.user.id ? ' marathon__row--me' : ''}`}
                >
                  <span className="board__pos">{i + 1}</span>
                  <span className="board__name">{r.display_name}</span>
                  <span className="marathon__cell">{r.wins || '–'}</span>
                  <span className="marathon__cell marathon__cell--opt">
                    {r.majors_played}/{r.majors_total}
                  </span>
                  <span className="marathon__cell marathon__cell--opt">{r.best_finish ?? '–'}</span>
                  <span className="marathon__cell marathon__cell--opt">{formatToPar(r.total_to_par_sum)}</span>
                  <span className="marathon__score">
                    {mode === 'points' ? r.points : r.placement_sum}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

    </div>
  )
}
